import AmountComparisonField from '../components/ComparisonField/AmountComparisonField';

import { type ILicenseTypeResponse } from '../../License/types/response';
import { ItemsTabFields } from '../../License/types/fieldEnums';
import { type IComparisonField } from '../types';

import formatNumber from '../../../helpers/formatNumber';

interface IGetItemFields {
  licenseType: ILicenseTypeResponse;
  t: (key: string) => string;
  invoiceCurrencyCode?: string;
  nationalCurrency: string;
  locale: string;
}

function getItemFields({
  licenseType,
  t,
  locale,
  invoiceCurrencyCode,
  nationalCurrency,
}: IGetItemFields): IComparisonField[][] {
  const {
    featureFlags: {
      packageManagementEnabled,
      itemWeightManagementEnabled,
      invoiceValueManagementEnabled,
      requestedAndApprovedAmountEnabled,
      remainingAmountEnabled,
    },
  } = licenseType;

  const itemFields: IComparisonField[][] = [
    [
      {
        label: 'Commodity Code',
        fieldKey: ItemsTabFields.commodityCode,
      },
      {
        label: 'Country Of Origin',
        fieldKey: ItemsTabFields.countryOfOriginCode,
        tagKey: ItemsTabFields.countryOfOriginName,
      },
      {
        label: 'Commodity Description',
        fieldKey: ItemsTabFields.commodityDescription,
        className: 'grid-full-width',
      },
      {
        label: 'Commercial Description (Goods Name)',
        fieldKey: ItemsTabFields.commercialDescription,
        className: 'grid-full-width',
      },
      {
        label: 'Manufacturers Name & Address',
        fieldKey: ItemsTabFields.manufacturerName,
        className: 'grid-full-width',
      },
    ],
  ];

  if (packageManagementEnabled) {
    itemFields.push([
      {
        label: 'Package Number',
        fieldKey: ItemsTabFields.packageNumber,
        render: (value: string) =>
          formatNumber({ value, locale, maximumFractionDigits: 0, minimumFractionDigits: 0 }) as string,
      },
      {
        label: 'Package type',
        fieldKey: ItemsTabFields.packageTypeCode,
        tagKey: ItemsTabFields.packageTypeName,
      },
      {
        label: 'Package Mark',
        fieldKey: ItemsTabFields.packageMark,
      },
    ]);
  }

  if (itemWeightManagementEnabled) {
    itemFields.push([
      {
        label: 'Gross Mass',
        fieldKey: ItemsTabFields.grossMass,
        render: (value: string) => `${formatNumber({ value, locale }) as string} ${t('KG')}`,
      },
      {
        label: 'Net Mass',
        fieldKey: ItemsTabFields.netMass,
        render: (value: string) => `${formatNumber({ value, locale }) as string} ${t('KG')}`,
      },
    ]);
  }

  if (invoiceValueManagementEnabled) {
    itemFields.push([
      {
        label: 'Invoice Value in Foreign Currency',
        fieldKey: ItemsTabFields.itemInvoiceAmountInForeignCurrency,
        render: (value: string) =>
          (formatNumber({ value, locale }) as string) + (invoiceCurrencyCode ? ` ${invoiceCurrencyCode}` : ''),
      },
      {
        label: 'Invoice Value in National Currency',
        fieldKey: ItemsTabFields.itemInvoiceAmountInNationalCurrency,
        render: (value: string) =>
          (formatNumber({ value, locale }) as string) + (nationalCurrency ? ` ${nationalCurrency}` : ''),
      },
    ]);
  }

  const quotaManagementFields: IComparisonField[] = [
    {
      label: 'Quota Type',
      fieldKey: ItemsTabFields.quotaType,
      render: (value: string) => t(value),
      className: 'grid-full-width',
    },
  ];

  if (requestedAndApprovedAmountEnabled) {
    quotaManagementFields.push(
      {
        label: 'Requested Amount or Quantity',
        fieldKey: ItemsTabFields.requestedAmount,
        uomCodeKey: ItemsTabFields.requestedUomCode,
        uomNameKey: ItemsTabFields.requestedUomName,
        className: 'grid-full-width',
        Component: AmountComparisonField,
      },
      {
        label: 'Approved Amount or Quantity',
        fieldKey: ItemsTabFields.approvedAmount,
        uomCodeKey: ItemsTabFields.approvedUomCode,
        uomNameKey: ItemsTabFields.approvedUomName,
        className: 'grid-full-width',
        Component: AmountComparisonField,
      }
    );
  }

  if (remainingAmountEnabled) {
    quotaManagementFields.push({
      label: 'Remaining Amount or Quantity',
      fieldKey: ItemsTabFields.remainingAmount,
      uomCodeKey: ItemsTabFields.approvedUomCode,
      uomNameKey: ItemsTabFields.approvedUomName,
      className: 'grid-full-width',
      Component: AmountComparisonField,
    });
  }

  itemFields.push(quotaManagementFields);

  return itemFields;
}

export default getItemFields;
